import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Check,
  FolderInput,
  Layers,
  PenLine,
  Plus,
  Tag,
  TriangleAlert,
  UserMinus,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import { Spinner } from '../../../components/ui';
import { govDate, plural } from '../format';
import type { HeadChoice, HeadPlan, NodePlan } from '../types';

const Step: React.FC<{ icon: React.ElementType; tone?: 'add' | 'end' | 'edit' | 'keep'; children: React.ReactNode }> = ({
  icon: Icon,
  tone = 'edit',
  children,
}) => (
  <li className="flex items-start gap-2 text-[12.5px] leading-snug text-neutral-600">
    <Icon
      className={`mt-px h-3.5 w-3.5 flex-shrink-0 ${
        tone === 'add'
          ? 'text-emerald-600'
          : tone === 'end'
            ? 'text-amber-600'
            : tone === 'keep'
              ? 'text-neutral-400'
              : 'text-sky-600'
      }`}
      strokeWidth={1.75}
    />
    <span className="min-w-0">{children}</span>
  </li>
);

const Name: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-ink">“{children}”</span>
);

/** The head research found, and whether recording it is part of this */
const HeadStep: React.FC<{
  head: HeadPlan;
  choice: HeadChoice;
  canEdit: boolean;
  onChange: (choice: HeadChoice) => void;
}> = ({ head, choice, canEdit, onChange }) => {
  const nothing = head.action === 'recorded' && head.closes.length === 0;
  const duplicate = head.person.candidates.some((candidate) => candidate.score >= 0.99);
  const ends = choice.closeAssignmentIds ?? head.closes.map((role) => role.assignmentId);
  const endsOn = head.endsOn ? govDate(head.endsOn) : 'today';

  if (nothing) {
    return (
      <Step icon={UserCheck} tone="keep">
        {head.person.id ? (
          <Link to={`/stakeholders/${head.person.id}`} className="text-ink hover:underline">
            {head.person.label}
          </Link>
        ) : (
          head.person.label
        )}{' '}
        is already recorded as {head.title}
      </Step>
    );
  }

  return (
    <li className="rounded-xl border border-neutral-200 px-3 py-2.5">
      <label className={`flex items-start gap-2 text-[12.5px] ${canEdit ? 'cursor-pointer' : ''}`}>
        <input
          type="checkbox"
          checked={choice.include}
          disabled={!canEdit}
          onChange={(event) => onChange({ ...choice, include: event.target.checked })}
          className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600/40"
        />
        <span className={`min-w-0 ${choice.include ? 'text-neutral-600' : 'text-neutral-400'}`}>
          {head.action === 'correct' ? (
            <>
              Correct when <span className="text-ink">{head.person.label}</span> took office:{' '}
              {govDate(head.correction?.from)} <ArrowRight className="inline h-3 w-3" strokeWidth={1.75} />{' '}
              <span className="text-ink">{govDate(head.correction?.to)}</span>
            </>
          ) : head.action === 'appoint' ? (
            <>
              Record <span className="text-ink">{head.person.label}</span>
              {head.person.isNew || choice.createPerson ? ' — new to the directory —' : ''} as {head.title}
              {head.since ? ` from ${govDate(head.since)}` : ''}
            </>
          ) : (
            <>
              <span className="text-ink">{head.person.label}</span> is recorded as {head.title}; the others
              recorded in that post leave it
            </>
          )}
          {head.sources === 0 && (
            <span className="mt-1 flex items-center gap-1 text-[11.5px] text-amber-700">
              <TriangleAlert className="h-3 w-3" strokeWidth={1.75} />
              Research cited no source for this
            </span>
          )}
          {head.pendingUpdateId && (
            <span className="mt-1 block text-[11.5px] text-neutral-400">
              The same change is waiting in Updates; this applies it
            </span>
          )}
        </span>
      </label>

      {choice.include && head.action === 'appoint' && head.person.candidates.length > 0 && canEdit && (
        <select
          aria-label="Who this is"
          value={choice.createPerson ? 'new' : (choice.stakeholderId ?? head.person.id ?? 'new')}
          onChange={(event) =>
            onChange({
              ...choice,
              createPerson: event.target.value === 'new',
              stakeholderId: event.target.value === 'new' ? null : event.target.value,
            })
          }
          className="mt-2 h-8 w-full rounded-lg border border-neutral-200 bg-white px-2 text-[16px] sm:text-[12.5px] text-ink focus:outline-none focus:ring-2 focus:ring-primary-600/40"
        >
          {head.person.id && <option value={head.person.id}>{head.person.label} (matched)</option>}
          {head.person.candidates.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.label} ({Math.round(candidate.score * 100)}%)
            </option>
          ))}
          <option value="new">Add {head.name} as a new person</option>
        </select>
      )}
      {duplicate && (
        <p className="mt-1.5 text-[11.5px] text-neutral-400">
          The directory holds this name more than once —{' '}
          <Link to="/duplicates" className="underline decoration-neutral-300 underline-offset-2 hover:text-ink">
            fold the duplicates
          </Link>
        </p>
      )}

      {choice.include && head.closes.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-neutral-100 pt-2">
          {head.closes.map((role) => {
            const checked = ends.includes(role.assignmentId);
            return (
              <li key={role.assignmentId}>
                <label className={`flex items-start gap-2 text-[12px] ${canEdit ? 'cursor-pointer' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!canEdit}
                    onChange={() =>
                      onChange({
                        ...choice,
                        closeAssignmentIds: checked
                          ? ends.filter((id) => id !== role.assignmentId)
                          : [...ends, role.assignmentId],
                      })
                    }
                    className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-600/40"
                  />
                  <span className={`min-w-0 ${checked ? 'text-neutral-600' : 'text-neutral-400'}`}>
                    <UserMinus className="mr-1 inline h-3 w-3 text-amber-600" strokeWidth={1.75} />
                    {checked ? 'Ends' : 'Keeps'} <span className="text-ink">{role.personLabel}</span> — {role.role}
                    {role.startDate ? ` since ${govDate(role.startDate)}` : ''}
                    {checked ? `, on ${endsOn}` : ''}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

/**
 * Everything applying an entity will do to the directory, in the order it
 * happens, as the server works it out — so "Update record" is never a leap:
 * the records made and renamed, where they are filed, who is recorded as the
 * head and whose role ends, on which day.
 */
export const EntityPlan: React.FC<{
  plan: NodePlan | null;
  loading: boolean;
  error?: string | null;
  applied: boolean;
  headChoice: HeadChoice | null;
  canEdit: boolean;
  onHeadChoice: (choice: HeadChoice) => void;
}> = ({ plan, loading, error, applied, headChoice, canEdit, onHeadChoice }) => {
  if (loading && !plan) {
    return (
      <p className="flex items-center gap-2 text-[12.5px] text-neutral-400">
        <Spinner className="h-3.5 w-3.5" /> Working out what would change
      </p>
    );
  }
  if (error) return <p className="text-[12.5px] text-primary-700">{error}</p>;
  if (!plan) return null;
  if (plan.blocked) {
    return (
      <p className="flex items-start gap-1.5 text-[12.5px] text-amber-700">
        <TriangleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
        {plan.blocked}
      </p>
    );
  }
  if (plan.record.action === 'skip') {
    return <p className="text-[12.5px] text-neutral-500">Left out of the directory — nothing is written.</p>;
  }

  const steps: React.ReactNode[] = [];
  plan.parentsFirst.forEach((name) =>
    steps.push(
      <Step key={`parent-${name}`} icon={Plus} tone="add">
        Creates <Name>{name}</Name> first — this sits under it
      </Step>,
    ),
  );
  if (plan.record.action === 'create') {
    steps.push(
      <Step key="create" icon={Plus} tone="add">
        Creates the record <Name>{plan.name}</Name>
      </Step>,
    );
  } else if (plan.record.action === 'recreate') {
    steps.push(
      <Step key="recreate" icon={Plus} tone="add">
        Makes the record <Name>{plan.name}</Name> again — it was deleted from the directory
      </Step>,
    );
  }
  plan.merges.forEach((merge) =>
    steps.push(
      <Step key={`merge-${merge.id}`} icon={Layers} tone="end">
        Folds <Name>{merge.label}</Name> into it{merge.people ? `, moving ${plural(merge.people, 'role')}` : ''}
      </Step>,
    ),
  );
  plan.duplicateRoles.forEach((role) =>
    steps.push(
      <Step key={`role-${role.personLabel}-${role.post}`} icon={UserCheck} tone="end">
        Keeps one role for <span className="text-ink">{role.personLabel}</span>, recorded {role.titles.length > 1 ? 'more than once' : 'twice'} as{' '}
        {role.post.toLowerCase()}
        <span className="text-neutral-400">
          {role.titles.length > 1 ? ` (${role.titles.map((title) => `“${title}”`).join(', ')})` : ''}
        </span>
      </Step>,
    ),
  );
  if (plan.rename) {
    steps.push(
      <Step key="rename" icon={PenLine}>
        Renames <Name>{plan.rename.from}</Name> to <Name>{plan.rename.to}</Name>
        <span className="text-neutral-400">
          {plan.rename.kind === 'official'
            ? ' — an official change, added to its name history'
            : ' — a correction, kept in the audit log'}
        </span>
      </Step>,
    );
  }
  if (plan.type && plan.record.action === 'keep') {
    steps.push(
      <Step key="type" icon={Tag}>
        Files it as {plan.type.to}
        {plan.type.from ? <span className="text-neutral-400">, not {plan.type.from}</span> : null}
      </Step>,
    );
  }
  if (plan.parent) {
    steps.push(
      <Step key="parent" icon={FolderInput}>
        {plan.parent.to
          ? plan.parent.from
            ? (
                <>
                  Moves it from under <Name>{plan.parent.from}</Name> to under <Name>{plan.parent.to}</Name>
                </>
              )
            : (
                <>
                  Files it under <Name>{plan.parent.to}</Name>
                </>
              )
          : (
              <>
                Takes it out from under <Name>{plan.parent.from}</Name> — the map puts it at the top
              </>
            )}
      </Step>,
    );
  }

  const nothing = !steps.length && (!plan.head || (plan.head.action === 'recorded' && !plan.head.closes.length));

  return (
    <div className="space-y-2">
      {plan.record.action === 'keep' && plan.record.id && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
          <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
          <span className="min-w-0 truncate">
            Record:{' '}
            <Link to={`/organizations/${plan.record.id}`} className="text-ink hover:underline">
              {plan.record.label}
            </Link>
          </span>
        </p>
      )}
      <ul className="space-y-1.5">
        {steps}
        {plan.head && headChoice && (
          <HeadStep head={plan.head} choice={headChoice} canEdit={canEdit} onChange={onHeadChoice} />
        )}
      </ul>
      {nothing && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
          {applied ? 'The record already matches the map.' : 'Nothing else to change.'}
        </p>
      )}
      {!plan.head && (
        <p className="flex items-center gap-1.5 text-[11.5px] text-neutral-400">
          <UserPlus className="h-3 w-3" strokeWidth={1.75} />
          Research named no head with a date to record
        </p>
      )}
    </div>
  );
};

/** True when applying would change nothing at all: the record already matches */
export const planIsNoop = (plan: NodePlan | null | undefined): boolean =>
  !!plan &&
  !plan.blocked &&
  plan.record.action === 'keep' &&
  !plan.merges.length &&
  !plan.duplicateRoles.length &&
  !plan.rename &&
  !plan.type &&
  !plan.parent &&
  !plan.parentsFirst.length &&
  (!plan.head || (plan.head.action === 'recorded' && !plan.head.closes.length));

/** What a head choice starts as: record it, as matched, ending whoever it ends */

export const defaultHeadChoice = (head: HeadPlan | null | undefined): HeadChoice | null =>
  head
    ? {
        include: head.action !== 'recorded' || head.closes.length > 0,
        stakeholderId: head.person.id,
        createPerson: head.person.isNew,
        closeAssignmentIds: head.closes.map((role) => role.assignmentId),
      }
    : null;
